import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Chip,
  Checkbox,
  IconButton,
  Tabs,
  Tab,
} from "@material-ui/core";
import { getImagenes } from "../../../api/imagen";
import { SERVER_URL } from "../../../api";
import { alertError } from "../../../utils/logger";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import DescriptionIcon from "@material-ui/icons/Description";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import SearchIcon from "@material-ui/icons/Search";
import { Autorenew as FilterOffIcon } from "@material-ui/icons";

function getData(imagenes) {
  let data = [];
  for (let i = 0; i < imagenes.length; ++i) {
    const elem = {};
    elem.titulo = imagenes[i].titulo || "";
    elem.url = imagenes[i].url;
    elem.id = imagenes[i]._id;
    elem.etiquetas = imagenes[i].etiquetas || [];
    elem.favorito = imagenes[i].favorito || false;
    elem.tipo = imagenes[i].tipo || "imagen";
    elem.tipoArchivo = imagenes[i].tipoArchivo || "";
    data = data.concat(elem);
  }
  return data;
}

function getFileIcon(tipo, tipoArchivo) {
  if (tipo === 'imagen') return null;
  
  const tipoLower = (tipoArchivo || '').toLowerCase();
  switch (tipoLower) {
    case 'pdf':
      return <PictureAsPdfIcon style={{ fontSize: 48, color: '#d32f2f' }} />;
    case 'doc':
    case 'docx':
      return <DescriptionIcon style={{ fontSize: 48, color: '#1976d2' }} />;
    case 'xls':
    case 'xlsx':
      return <DescriptionIcon style={{ fontSize: 48, color: '#388e3c' }} />;
    case 'ppt':
    case 'pptx':
      return <DescriptionIcon style={{ fontSize: 48, color: '#f57c00' }} />;
    default:
      return <InsertDriveFileIcon style={{ fontSize: 48, color: '#757575' }} />;
  }
}

export default function ImageStorageSelectorDialog({
  open,
  onClose,
  onSelect,
  filterType = null, // 'imagen' o 'archivo' o null para ambos
  title = "Seleccionar imagen o archivo",
}) {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedTipo, setSelectedTipo] = useState(filterType || null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 20;

  useEffect(() => {
    if (open) {
      loadImages();
    }
  }, [open, currentPage, searchText, selectedTipo, selectedTags]);

  const loadImages = () => {
    setLoading(true);
    const params = {
      page: currentPage,
      limit: pageSize,
      getDisabled: "true",
    };
    
    if (searchText) {
      params.search = searchText;
    }
    if (selectedTipo) {
      params.tipo = selectedTipo;
    }
    if (selectedTags.length > 0) {
      params.tag = selectedTags.join(',');
    }

    getImagenes(params)
      .then((res) => {
        if (res.status === 200) {
          const responseData = res.data.data || res.data;
          const processedData = getData(responseData);
          setData(processedData);
          setFilteredData(processedData);
          
          if (res.data.pagination) {
            setTotalPages(res.data.pagination.pages);
          }
          
          setLoading(false);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se han podido obtener las imágenes.",
        });
        setLoading(false);
      });
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTagToFilter();
    }
  };

  const addTagToFilter = () => {
    const tagValue = tagInput.trim().toLowerCase();
    if (tagValue && !selectedTags.includes(tagValue)) {
      setSelectedTags([...selectedTags, tagValue]);
      setTagInput("");
      setCurrentPage(1);
    } else if (tagValue) {
      setTagInput("");
    }
  };

  const removeTagFromFilter = (tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedTags([]);
    setTagInput("");
    setSelectedTipo(filterType || null);
    setCurrentPage(1);
  };

  const handleSelect = () => {
    if (selectedId) {
      const selected = data.find(item => item.id === selectedId);
      if (selected) {
        onSelect(selected.url);
        onClose();
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: { maxHeight: "90vh" }
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {/* Filtros */}
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", gap: "10px", marginBottom: "10px", flexWrap: "wrap" }}>
            <TextField
              id="search"
              label="Buscar"
              placeholder="Buscar por título o URL..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              margin="dense"
              variant="outlined"
              style={{ flex: 1, minWidth: "200px" }}
              InputProps={{
                endAdornment: <SearchIcon style={{ color: "#999" }} />
              }}
            />
            {!filterType && (
              <div style={{ minWidth: "150px" }}>
                <select
                  value={selectedTipo || ""}
                  onChange={(e) => {
                    setSelectedTipo(e.target.value || null);
                    setCurrentPage(1);
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    fontSize: "14px"
                  }}
                >
                  <option value="">Todos</option>
                  <option value="imagen">Imágenes</option>
                  <option value="archivo">Archivos</option>
                </select>
              </div>
            )}
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<FilterOffIcon />}
              style={{ borderColor: "#f3691a", color: "#f3691a" }}
            >
              Limpiar
            </Button>
          </div>
          
          {/* Tags filter */}
          <div style={{ marginTop: "10px" }}>
            <div style={{ 
              border: "1px solid #ced4da", 
              borderRadius: "4px", 
              padding: "8px",
              minHeight: "40px",
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              alignItems: "center",
              backgroundColor: "#fff"
            }}>
              {selectedTags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => removeTagFromFilter(tag)}
                  size="small"
                  style={{ 
                    backgroundColor: "#f3691a",
                    color: "#fff",
                    fontSize: "12px",
                    height: "24px"
                  }}
                />
              ))}
              <TextField
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                placeholder={selectedTags.length === 0 ? "Filtrar por etiqueta..." : "Añadir otra etiqueta..."}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  style: { fontSize: "14px" }
                }}
                style={{ 
                  flex: 1,
                  minWidth: "150px"
                }}
              />
            </div>
          </div>
        </div>

        {/* Grid de imágenes/archivos */}
        {loading ? (
          <Typography style={{ textAlign: "center", padding: "40px" }}>
            Cargando...
          </Typography>
        ) : filteredData.length === 0 ? (
          <Typography style={{ textAlign: "center", padding: "40px", color: "#999" }}>
            No se encontraron imágenes o archivos
          </Typography>
        ) : (
          <>
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", 
              gap: "15px",
              maxHeight: "400px",
              overflowY: "auto",
              padding: "10px"
            }}>
              {filteredData.map((item) => {
                const isSelected = selectedId === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => setSelectedId(item.id)}
                    style={{
                      position: "relative",
                      border: isSelected ? "3px solid #f3691a" : "2px solid #ddd",
                      borderRadius: "8px",
                      overflow: "hidden",
                      cursor: "pointer",
                      backgroundColor: "#fff",
                      transition: "all 0.2s",
                    }}
                  >
                    {isSelected && (
                      <div style={{
                        position: "absolute",
                        top: 5,
                        right: 5,
                        zIndex: 10,
                        backgroundColor: "#f3691a",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "14px",
                        fontWeight: "bold"
                      }}>
                        ✓
                      </div>
                    )}
                    {item.tipo === 'imagen' ? (
                      <img
                        src={SERVER_URL + "/" + item.url}
                        alt={item.titulo || "Imagen"}
                        style={{
                          width: "100%",
                          height: "150px",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      <div style={{
                        width: "100%",
                        height: "150px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                      }}>
                        {getFileIcon(item.tipo, item.tipoArchivo)}
                        {item.tipoArchivo && (
                          <span style={{ fontSize: "10px", marginTop: "8px", color: "#666" }}>
                            {item.tipoArchivo.toUpperCase()}
                          </span>
                        )}
                      </div>
                    )}
                    <div style={{ padding: "8px" }}>
                      <Typography 
                        variant="caption" 
                        style={{ 
                          display: "block",
                          fontWeight: "bold",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {item.titulo || "Sin título"}
                      </Typography>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Paginación */}
            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px", marginTop: "15px" }}>
                <Button
                  variant="outlined"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{ borderColor: "#f3691a", color: "#f3691a" }}
                >
                  Anterior
                </Button>
                <Typography>
                  Página {currentPage} de {totalPages}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  style={{ borderColor: "#f3691a", color: "#f3691a" }}
                >
                  Siguiente
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSelect}
          variant="contained"
          disabled={!selectedId}
          style={{ backgroundColor: "#f3691a", color: "white" }}
        >
          Seleccionar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
